import { useQuery } from "@tanstack/react-query";

import EncoderAPI from "../../api/EncoderAPI";
import MediaAPI from "../../api/MediaAPI";

import type { PollingFlags } from "./live.types";

export const LIVE_DETAILS_INTERVAL_MS = 5000;
const LIVE_STATS_INTERVAL_MS = 2500;

export default function useLiveQueries(
  mediaId: string,
  flags: PollingFlags,
  quotaRefusedAt: number | null,
) {
  // Same key as ContentPage: this observer only adds refetch triggers and
  // reads the cache, it never starts a competing fetch on its own.
  const mediaQuery = useQuery(
    MediaAPI.generateQuery({
      fn: MediaAPI.getMedia,
      params: [mediaId],
      options: {
        enabled: Boolean(mediaId),
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: flags.mediaIntervalMs,
      },
    }),
  );
  const planQuery = useQuery(
    EncoderAPI.generateQuery({
      fn: EncoderAPI.getEncodingPlan,
      params: [],
      options: {
        enabled: flags.plan,
        staleTime: Infinity,
        refetchOnWindowFocus: quotaRefusedAt !== null ? "always" : false,
      },
    }),
  );
  const detailsQuery = useQuery(
    EncoderAPI.generateQuery({
      fn: EncoderAPI.getLiveDetails,
      params: [mediaId],
      options: {
        enabled: flags.details,
        retry: false,
        refetchInterval: flags.details ? LIVE_DETAILS_INTERVAL_MS : false,
        refetchOnWindowFocus: false,
      },
    }),
  );
  const statsQuery = useQuery(
    EncoderAPI.generateQuery({
      fn: EncoderAPI.getLiveStats,
      params: [mediaId],
      options: {
        enabled: flags.stats,
        refetchInterval: flags.stats ? LIVE_STATS_INTERVAL_MS : false,
        refetchOnWindowFocus: false,
      },
    }),
  );

  return { mediaQuery, detailsQuery, statsQuery, planQuery };
}
